import React, { useEffect, useRef } from 'react';

/**
 * CreativeCanvasPreview — aperçu live d'une créa, rendu côté client en <canvas>.
 *
 * Reproduit le template par défaut du backend (carré 1080×1080 : image produit,
 * nom, badge prix vert) à l'échelle. Donne un retour visuel immédiat avant la
 * génération serveur (PNG/MP4).
 */
const BASE = 1080; // dimension logique du template backend

const fmtPrix = (n) =>
  (n || n === 0 ? new Intl.NumberFormat('fr-FR').format(n) + ' XOF' : '— XOF');

function roundedRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function wrapText(ctx, text, maxWidth, maxLines = 2) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let current = '';
  for (const w of words) {
    const test = current ? `${current} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = w;
      if (lines.length === maxLines) break;
    } else {
      current = test;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);
  return lines.length ? lines : [''];
}

export default function CreativeCanvasPreview({ nom, prix, imageUrl, size = 320 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const s = size / BASE; // facteur d'échelle template → canvas

    let cancelled = false;

    const draw = (img) => {
      if (cancelled) return;
      // Fond
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(0, 0, size, size);

      // Zone image (x90 y90 w900 h640 r24)
      const ix = 90 * s, iy = 90 * s, iw = 900 * s, ih = 640 * s, ir = 24 * s;
      ctx.save();
      roundedRect(ctx, ix, iy, iw, ih, ir);
      ctx.clip();
      if (img) {
        // cover
        const scale = Math.max(iw / img.width, ih / img.height);
        const dw = img.width * scale, dh = img.height * scale;
        ctx.drawImage(img, ix + (iw - dw) / 2, iy + (ih - dh) / 2, dw, dh);
      } else {
        ctx.fillStyle = '#1e293b';
        ctx.fillRect(ix, iy, iw, ih);
        ctx.fillStyle = '#475569';
        ctx.font = `${28 * s * 3}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('🛍️', ix + iw / 2, iy + ih / 2);
        ctx.textAlign = 'start';
      }
      ctx.restore();

      // Nom (x90 y850 size64 bold blanc) — wrap 2 lignes
      ctx.fillStyle = '#ffffff';
      ctx.textBaseline = 'alphabetic';
      ctx.font = `bold ${64 * s}px sans-serif`;
      const lines = wrapText(ctx, nom || 'Nom du produit', 900 * s, 2);
      let ty = 845 * s;
      for (const line of lines) {
        ctx.fillText(line, 90 * s, ty);
        ty += 64 * 1.15 * s;
      }

      // Badge prix (rect x90 y930 w520 h96 r16 vert)
      ctx.fillStyle = '#22c55e';
      roundedRect(ctx, 90 * s, 930 * s, 520 * s, 96 * s, 16 * s);
      ctx.fill();
      ctx.fillStyle = '#0f172a';
      ctx.font = `bold ${56 * s}px sans-serif`;
      ctx.fillText(fmtPrix(prix), 120 * s, 998 * s);
    };

    if (imageUrl) {
      const img = new Image();
      img.onload = () => draw(img);
      img.onerror = () => draw(null);
      img.src = imageUrl;
    } else {
      draw(null);
    }

    return () => { cancelled = true; };
  }, [nom, prix, imageUrl, size]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: size, height: size, borderRadius: 12, display: 'block' }}
    />
  );
}
